import { useState, type ElementType, type KeyboardEvent } from 'react'

import { Avatar } from '@components/atoms/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/atoms/DropdownMenu'
import { Input } from '@components/atoms/Input'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@components/atoms/Sidebar'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('components.sidebar.group_switcher')

export type GroupSwitcherOption = { id: string; name: string; href: string }

export type GroupSwitcherProps = {
  current: Omit<GroupSwitcherOption, 'href'> | null
  groups: GroupSwitcherOption[]
  all?: { label: string; href: string }
  switchable: boolean
  linkComponent?: ElementType<{ href: string }>
}

function CurrentScope({ name, organisation }: { name: string; organisation: boolean }) {
  return (
    <>
      {organisation ? (
        <Icon type="bank" width={20} height={20} aria-hidden className="tw:mx-1.5 tw:shrink-0" />
      ) : (
        <Avatar
          name={name}
          className="tw:h-8 tw:w-8 tw:rounded-md tw:bg-sidebar-primary tw:text-sm tw:text-sidebar-primary-foreground"
        />
      )}
      <span className="tw:flex-1 tw:truncate tw:font-medium tw:group-data-[collapsible=icon]:hidden">{name}</span>
    </>
  )
}

function focusSoon(node: HTMLInputElement | null) {
  if (node) setTimeout(() => node.focus())
}

function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key.length === 1) event.stopPropagation()
  if (event.key !== 'ArrowDown') return
  event.preventDefault()
  event.currentTarget.nextElementSibling?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
}

export function GroupSwitcher({
  current,
  groups,
  all,
  switchable,
  linkComponent: LinkComponent = 'a',
}: GroupSwitcherProps) {
  const name = current?.name ?? all?.label ?? t('your_groups')
  const [query, setQuery] = useState('')
  const searchable = groups.length > 10
  const shown = groups.filter((group) => group.name.toLowerCase().includes(query.trim().toLowerCase()))

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {switchable ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                tooltip={t('change_view')}
                aria-label={`${t('change_view')}: ${name}`}
                className="tw:border tw:border-sidebar-border"
              >
                <CurrentScope name={name} organisation={!current} />
                <Icon
                  type="chevron-down"
                  width={16}
                  height={16}
                  aria-hidden
                  className="tw:opacity-70 tw:group-data-[collapsible=icon]:hidden"
                />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="tw:min-w-[240px]" onCloseAutoFocus={() => setQuery('')}>
              <DropdownMenuLabel>{t('change_view')}</DropdownMenuLabel>
              {all && (
                <>
                  <DropdownMenuItem asChild className="tw:gap-2">
                    <LinkComponent href={all.href} aria-current={current ? undefined : 'true'}>
                      <Icon type="bank" width={16} height={16} aria-hidden className="tw:text-gray-400" />
                      {all.label}
                    </LinkComponent>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuLabel className="tw:text-xs tw:font-medium tw:text-gray-500">
                {t('your_groups')}
              </DropdownMenuLabel>
              {searchable && (
                <Input
                  ref={focusSoon}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={t('search_groups')}
                  aria-label={t('search_groups')}
                  className="tw:mb-1 tw:h-8"
                />
              )}
              <div className={searchable ? 'tw:max-h-72 tw:overflow-y-auto' : undefined}>
                {shown.map((group) => (
                  <DropdownMenuItem key={group.id} asChild className="tw:justify-between tw:gap-2">
                    <LinkComponent href={group.href} aria-current={group.id === current?.id ? 'true' : undefined}>
                      {group.name}
                      {group.id === current?.id && <Icon type="check" width={16} height={16} aria-hidden />}
                    </LinkComponent>
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div
            title={name}
            className="tw:flex tw:h-12 tw:items-center tw:gap-2 tw:p-2 tw:text-sm tw:text-sidebar-foreground tw:group-data-[collapsible=icon]:justify-center tw:group-data-[collapsible=icon]:p-0"
          >
            <CurrentScope name={name} organisation={!current} />
          </div>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
